import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import BottomSheet from "@/components/BottomSheet";
import {
  parseNominal,
  formatNominalInput,
  todayYMD,
} from "@/lib/format";
import { Camera } from "lucide-react";

const SUMBER_OPTIONS = [
  { v: "gaji", l: "Gaji bulanan" },
  { v: "bonus", l: "Bonus" },
  { v: "fee", l: "Fee project" },
  { v: "bisnis", l: "Hasil bisnis" },
  { v: "lain", l: "Lainnya" },
];

export default function TransaksiForm({
  open,
  onClose,
  defaultTipe = "pengeluaran",
  rekeningList = [],
  onSaved,
  existing = null,
}) {
  const [tipe, setTipe] = useState(existing?.tipe || defaultTipe);
  const [tanggal, setTanggal] = useState(existing?.tanggal || todayYMD());
  const [deskripsi, setDeskripsi] = useState(existing?.deskripsi || "");
  const [nominal, setNominal] = useState(
    existing?.nominal ? formatNominalInput(existing.nominal) : ""
  );
  const [rekeningId, setRekeningId] = useState(
    existing?.rekening_id || rekeningList[0]?.id || ""
  );
  const [rekeningAsalId, setRekeningAsalId] = useState(
    existing?.rekening_asal_id || rekeningList[0]?.id || ""
  );
  const [rekeningTujuanId, setRekeningTujuanId] = useState(
    existing?.rekening_tujuan_id || rekeningList[1]?.id || ""
  );
  const [sumber, setSumber] = useState(existing?.sumber || "gaji");
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = React.useRef(null);

  const fetchSuggestions = (val) => {
    console.log("fetch called:", val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!val || val.length < 2) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`/suggestions/deskripsi?q=${encodeURIComponent(val)}`);
        console.log("suggestions:", res.data);
        setSuggestions(res.data || []);
        setShowSuggestions(true);
      } catch { setSuggestions([]); }
    }, 300);
  };
  useEffect(() => {
    if (open) {
      setTipe(existing?.tipe || defaultTipe);
      setTanggal(existing?.tanggal || todayYMD());
      setDeskripsi(existing?.deskripsi || "");
      setNominal(existing?.nominal ? formatNominalInput(existing.nominal) : "");
      setRekeningId(existing?.rekening_id || rekeningList[0]?.id || "");
      setRekeningAsalId(existing?.rekening_asal_id || rekeningList[0]?.id || "");
      setRekeningTujuanId(
        existing?.rekening_tujuan_id ||
          rekeningList.find((r) => r.id !== (existing?.rekening_asal_id || rekeningList[0]?.id))?.id ||
          ""
      );
      setSumber(existing?.sumber || "gaji");
    }
  }, [open, defaultTipe, existing, rekeningList]);

  // Auto-reset tujuan if same as asal
  useEffect(() => {
    if (tipe === "transfer" && rekeningTujuanId === rekeningAsalId) {
      const alt = rekeningList.find((r) => r.id !== rekeningAsalId);
      setRekeningTujuanId(alt ? alt.id : "");
    }
  }, [rekeningAsalId, tipe, rekeningList, rekeningTujuanId]);

  const tujuanOptions = useMemo(
    () => rekeningList.filter((r) => r.id !== rekeningAsalId),
    [rekeningList, rekeningAsalId]
  );

  const submit = async (e) => {
    e.preventDefault();
    const nom = parseNominal(nominal);
    if (nom <= 0) return toast.error("Nominal harus lebih dari 0");

    setBusy(true);
    try {
      let payload;
      if (tipe === "transfer") {
        if (!rekeningAsalId || !rekeningTujuanId)
          throw new Error("Pilih rekening asal dan tujuan");
        if (rekeningAsalId === rekeningTujuanId)
          throw new Error("Rekening asal dan tujuan harus berbeda");
        payload = {
          tipe: "transfer",
          nominal: nom,
          rekening_asal_id: rekeningAsalId,
          rekening_tujuan_id: rekeningTujuanId,
          tanggal,
          deskripsi: deskripsi.trim() || null,
        };
      } else {
        if (!deskripsi.trim()) throw new Error("Deskripsi wajib diisi");
        if (!rekeningId) throw new Error("Pilih rekening terlebih dahulu");
        payload = {
          tipe,
          deskripsi: deskripsi.trim(),
          nominal: nom,
          rekening_id: rekeningId,
          tanggal,
          sumber: tipe === "pemasukan" ? sumber : null,
        };
      }

      if (existing) {
        await api.put(`/transaksi/${existing.id}`, payload);
        toast.success("Transaksi diperbarui");
      } else {
        await api.post("/transaksi", payload);
        toast.success("Transaksi disimpan");
      }
      onClose();
      onSaved && onSaved();
    } catch (err) {
      const msg =
        formatApiErrorDetail(err.response?.data?.detail) || err.message || "Gagal menyimpan";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const asalNama =
    rekeningList.find((r) => r.id === rekeningAsalId)?.nama || "";
  const tujuanNama =
    rekeningList.find((r) => r.id === rekeningTujuanId)?.nama || "";

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={existing ? "Edit Transaksi" : "Tambah Transaksi"}
      testId="transaksi-form-sheet"
    >
      <form onSubmit={submit} className="space-y-4" data-testid="transaksi-form">
        {/* Toggle 3 opsi */}
        <div className="grid grid-cols-3 gap-1 p-1 bg-slate-100 rounded-xl">
          <button
            type="button"
            onClick={() => setTipe("pemasukan")}
            data-testid="tipe-pemasukan"
            className={`h-9 rounded-lg text-[11px] font-bold tap ${
              tipe === "pemasukan" ? "text-white shadow" : "text-slate-500"
            }`}
            style={{
              backgroundColor: tipe === "pemasukan" ? "#375623" : "transparent",
            }}
          >
            Pemasukan
          </button>
          <button
            type="button"
            onClick={() => setTipe("pengeluaran")}
            data-testid="tipe-pengeluaran"
            className={`h-9 rounded-lg text-[11px] font-bold tap ${
              tipe === "pengeluaran" ? "text-white shadow" : "text-slate-500"
            }`}
            style={{
              backgroundColor:
                tipe === "pengeluaran" ? "#C00000" : "transparent",
            }}
          >
            Pengeluaran
          </button>
          <button
            type="button"
            onClick={() => setTipe("transfer")}
            data-testid="tipe-transfer"
            className={`h-9 rounded-lg text-[11px] font-bold tap ${
              tipe === "transfer" ? "text-white shadow" : "text-slate-500"
            }`}
            style={{
              backgroundColor: tipe === "transfer" ? "#1F4E5F" : "transparent",
            }}
          >
            Transfer
          </button>
        </div>

        {/* Scan nota placeholder (only for non-transfer) */}
        {tipe !== "transfer" && (
          <div
            className="rounded-xl p-3 flex items-center gap-3 border border-dashed border-slate-300 bg-slate-50"
            data-testid="scan-nota-placeholder"
          >
            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center text-slate-400">
              <Camera size={18} />
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-700">
                Scan Nota
              </div>
              <div className="text-[10px] text-slate-400">
                Fitur segera hadir
              </div>
            </div>
          </div>
        )}

        <Field label="Tanggal">
          <input
            type="date"
            value={tanggal}
            onChange={(e) => setTanggal(e.target.value)}
            data-testid="input-tanggal"
            className={inputCls}
            required
          />
        </Field>

        {tipe === "transfer" ? (
          <>
            <Field label="Dari rekening">
              <select
                value={rekeningAsalId}
                onChange={(e) => setRekeningAsalId(e.target.value)}
                data-testid="input-rekening-asal"
                className={inputCls}
                required
              >
                <option value="">— Pilih rekening asal —</option>
                {rekeningList.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.nama} ({r.bank})
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Ke rekening">
              <select
                value={rekeningTujuanId}
                onChange={(e) => setRekeningTujuanId(e.target.value)}
                data-testid="input-rekening-tujuan"
                className={inputCls}
                required
              >
                <option value="">— Pilih rekening tujuan —</option>
                {tujuanOptions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.nama} ({r.bank})
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Nominal (Rp)">
              <input
                type="text"
                inputMode="numeric"
                value={nominal}
                onChange={(e) => setNominal(formatNominalInput(e.target.value))}
                data-testid="input-nominal"
                className={inputCls}
                placeholder="0"
                required
              />
            </Field>
            <Field label="Deskripsi">
  <div className="relative">
    <input
      type="text"
      value={deskripsi}
      onChange={(e) => {
        setDeskripsi(e.target.value);
        fetchSuggestions(e.target.value);
      }}
      onFocus={() => deskripsi.length >= 2 && setShowSuggestions(true)}
      onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
      data-testid="input-deskripsi"
      className={inputCls}
      placeholder="mis. Makan siang di warung"
      required
    />
    {showSuggestions && suggestions.length > 0 && (
      <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
        {suggestions.map((s, i) => (
          <button
            key={i}
            type="button"
            onMouseDown={() => {
              setDeskripsi(s);
              setShowSuggestions(false);
              setSuggestions([]);
            }}
            className="w-full text-left px-3 py-2.5 text-xs hover:bg-slate-50 border-b border-slate-100 last:border-0"
            style={{ color: "#111827" }}
          >
            {s}
          </button>
        ))}
      </div>
    )}
  </div>
</Field>
          </>
        ) : (
          <>
            <Field label="Deskripsi">
  <div className="relative">
    <input
      type="text"
      value={deskripsi}
      onChange={(e) => {
        setDeskripsi(e.target.value);
        fetchSuggestions(e.target.value);
      }}
      onFocus={() => deskripsi.length >= 2 && setShowSuggestions(true)}
      onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
      data-testid="input-deskripsi"
      className={inputCls}
      placeholder="mis. Makan siang di warung"
      required
    />
    {showSuggestions && suggestions.length > 0 && (
      <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
        {suggestions.map((s, i) => (
          <button
            key={i}
            type="button"
            onMouseDown={() => {
              setDeskripsi(s);
              setShowSuggestions(false);
              setSuggestions([]);
            }}
            className="w-full text-left px-3 py-2.5 text-xs hover:bg-slate-50 border-b border-slate-100 last:border-0"
            style={{ color: "#111827" }}
          >
            {s}
          </button>
        ))}
      </div>
    )}
  </div>
</Field>
            <Field label="Nominal (Rp)">
              <input
                type="text"
                inputMode="numeric"
                value={nominal}
                onChange={(e) => setNominal(formatNominalInput(e.target.value))}
                data-testid="input-nominal"
                className={inputCls}
                placeholder="0"
                required
              />
            </Field>
            <Field label="Rekening">
              <select
                value={rekeningId}
                onChange={(e) => setRekeningId(e.target.value)}
                data-testid="input-rekening"
                className={inputCls}
                required
              >
                <option value="">— Pilih rekening —</option>
                {rekeningList.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.nama} ({r.bank})
                  </option>
                ))}
              </select>
            </Field>
            {tipe === "pemasukan" && (
              <Field label="Sumber pemasukan">
                <select
                  value={sumber}
                  onChange={(e) => setSumber(e.target.value)}
                  data-testid="input-sumber"
                  className={inputCls}
                >
                  {SUMBER_OPTIONS.map((s) => (
                    <option key={s.v} value={s.v}>
                      {s.l}
                    </option>
                  ))}
                </select>
              </Field>
            )}
          </>
        )}

        <button
          type="submit"
          disabled={busy}
          data-testid="transaksi-submit"
          className="w-full h-11 rounded-xl font-semibold text-sm text-white tap disabled:opacity-60"
          style={{ backgroundColor: "#1F4E5F" }}
        >
          {busy ? "Menyimpan..." : existing ? "Simpan Perubahan" : "Simpan"}
        </button>
      </form>
    </BottomSheet>
  );
}

const inputCls =
  "mt-1 w-full h-11 px-3 rounded-xl border border-slate-200 focus:border-[#1F4E5F] focus:outline-none text-sm bg-white";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-slate-600">{label}</span>
      {children}
    </label>
  );
}
